"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.InputArea = void 0;
require("./InputArea.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _MessageInline = _interopRequireDefault(require("../MessageInline/MessageInline"));
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * InputArea Component
 * @method
 * @param {InputAreaProps} props
 * @returns {JSX.Element} InputArea
 */
const InputArea = props => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    onPaste,
    onClick,
    inputRef,
    name,
    id,
    disabled,
    readOnly,
    required,
    placeholder,
    autoComplete,
    autoCorrect,
    autoCapitalize,
    spellCheck,
    tabIndex,
    label,
    ariaLabel,
    ariaDescribedBy,
    message,
    messageType = defaultProps.messageType,
    // 'error' | 'warning' | 'success' | 'info'
    helperText,
    rows = defaultProps.rows,
    maxLength,
    className,
    inputClassName
  } = props;
  const inputId = id || name || 'inputArea';
  const [active, setActive] = (0, _react.useState)(false);
  const handleChange = e => {
    if (disabled || readOnly) return;
    if (!active) setActive(true);
    onChange && onChange(e);
  };
  const handleFocus = e => {
    setActive(true);
    onFocus && onFocus(e);
  };
  const handleBlur = e => {
    setActive(false);
    onBlur && onBlur(e);
  };
  const handlePaste = e => {
    onPaste && onPaste(e);
  };
  const componentClassName = (0, _classnames.default)('pal-c-InputArea', className, {
    'pal-c-InputArea--disabled': disabled,
    'pal-c-InputArea--readOnly': readOnly,
    'pal-c-InputArea--required': required,
    'pal-c-InputArea--active': active,
    'pal-c-InputArea--entered': value?.length > 0
  });
  const renderHelperText = () => {
    if (!helperText) return null;
    return /*#__PURE__*/_react.default.createElement("p", {
      className: "pal-c-InputArea__helper"
    }, helperText);
  };
  const renderMessage = () => {
    if (!message) return null;
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-InputArea__message"
    }, /*#__PURE__*/_react.default.createElement(_MessageInline.default, {
      message: message,
      variant: messageType
    }));
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName
  }, label && /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: inputId,
    className: "pal-c-InputArea__label"
  }, label), /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-InputArea__control"
  }, /*#__PURE__*/_react.default.createElement("textarea", {
    ref: inputRef,
    id: inputId,
    name: name,
    className: (0, _classnames.default)('pal-c-InputArea__textarea', inputClassName),
    value: value ?? '',
    placeholder: placeholder,
    autoComplete: autoComplete,
    autoCorrect: autoCorrect,
    autoCapitalize: autoCapitalize,
    spellCheck: spellCheck,
    rows: rows,
    maxLength: maxLength,
    onClick: onClick,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onPaste: handlePaste,
    tabIndex: tabIndex,
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    "aria-label": label ? undefined : ariaLabel,
    "aria-describedby": ariaDescribedBy
  })), renderHelperText(), renderMessage());
};

/**
 * Property type definitions
 * @typedef InputAreaProps
 * @type {object}
 * @property {string} id - Element id. Defaults to `name` or "inputArea".
 * @property {string} name - Textarea name attribute (also used to derive default `id`).
 * @property {string} value - Controlled value provided by the parent.
 * @property {function} onChange - Change handler (native event).
 * @property {function} onFocus - Focus handler (native event).
 * @property {function} onBlur - Blur handler (native event).
 * @property {function} onPaste - Paste handler (native event).
 * @property {function} onClick - Click handler (native event).
 * @property {any} inputRef - RefObject to the underlying textarea.
 * @property {boolean} disabled - Disables the textarea and actions.
 * @property {boolean} readOnly - Makes the textarea read-only.
 * @property {boolean} required - Adds `required` attribute.
 * @property {string} placeholder - Placeholder text.
 * @property {string} autoComplete - Browser autocomplete hint.
 * @property {string} autoCorrect - Native autocorrect (iOS).
 * @property {string} autoCapitalize - Native autocapitalize.
 * @property {boolean} spellCheck - Native spellcheck.
 * @property {number} tabIndex - Native tabIndex.
 * @property {string} label - Visible label text (provide `ariaLabel` when omitted).
 * @property {string} ariaLabel - Accessible label used when there is no visible label.
 * @property {string} ariaDescribedBy - Sets aria-describedby for the textarea.
 * @property {string} message - Helper/warning/error content rendered below the control.
 * @property {string} messageType - Styles the message and controls ARIA attributes.
 * @property {string} helperText - Small helper text below the textarea.
 * @property {number} rows - Initial number of visible lines.
 * @property {number} cols - Width of the textarea in characters (native).
 * @property {string} wrap - Text wrapping behavior (native).
 * @property {number} maxLength - Max characters (native).
 * @property {string} defaultValue - Uncontrolled initial value (avoid with `value`).
 * @property {string} className - Extra class for the root container.
 * @property {string} inputClassName - Extra class for the textarea element.
 */
exports.InputArea = InputArea;
const propTypes = exports.propTypes = {
  /**
   * Textarea name attribute (also used to derive default `id`).
  */
  name: _propTypes.default.string,
  /**
   * Element id. Defaults to `name` or "inputArea".
  */
  id: _propTypes.default.string,
  /**
   * Controlled value provided by the parent.
  */
  value: _propTypes.default.string,
  /**
   * Change handler (native event).
  */
  onChange: _propTypes.default.func,
  /**
   * Focus handler (native event).
  */
  onFocus: _propTypes.default.func,
  /**
   * Blur handler (native event).
  */
  onBlur: _propTypes.default.func,
  /**
   * Paste handler (native event).
  */
  onPaste: _propTypes.default.func,
  /**
   * Click handler (native event).
  */
  onClick: _propTypes.default.func,
  /**
   * RefObject to the underlying textarea.
  */
  inputRef: _propTypes.default.any,
  /**
   * Disables the textarea and actions.
  */
  disabled: _propTypes.default.bool,
  /**
   * Makes the textarea read-only.
  */
  readOnly: _propTypes.default.bool,
  /**
   * Adds `required` attribute.
  */
  required: _propTypes.default.bool,
  /**
   * Placeholder text.
  */
  placeholder: _propTypes.default.string,
  /**
   * Browser autocomplete hint.
  */
  autoComplete: _propTypes.default.string,
  /**
   * Native autocorrect (iOS).
  */
  autoCorrect: _propTypes.default.string,
  /**
   * Native autocapitalize.
  */
  autoCapitalize: _propTypes.default.string,
  /**
   * Native spellcheck attribute.
  */
  spellCheck: _propTypes.default.bool,
  /**
   * Native tabIndex attribute.
  */
  tabIndex: _propTypes.default.number,
  /**
   * Visible label text.
  */
  label: _propTypes.default.string,
  /**
   * Accessible label used when there is no visible label.
  */
  ariaLabel: _propTypes.default.string,
  /**
   * Sets aria-describedby for the textarea.
  */
  ariaDescribedBy: _propTypes.default.string,
  /**
   * Small helper text below the textarea.
  */
  helperText: _propTypes.default.string,
  /**
     * Helper/warning/error content rendered below the control.
     * Accepts plain text or a React node.
     */
  message: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  /**
     * Visual/semantic state for the message area and ARIA flags.
     * - error → sets `aria-invalid="true"`
     * - warning / success / info → style-only states
     */
  messageType: _propTypes.default.oneOf(['error', 'warning', 'success', 'info']),
  /**
   * Initial number of visible lines.
  */
  rows: _propTypes.default.number,
  /**
   * Width of the textarea in characters (native).
  */
  cols: _propTypes.default.number,
  /**
   * Text wrapping behavior (native).
  */
  wrap: _propTypes.default.string,
  /**
   * Max characters (native).
  */
  maxLength: _propTypes.default.number,
  /**
   * Uncontrolled initial value.
  */
  defaultValue: _propTypes.default.string,
  /**
   * Extra class for the root container.
  */
  className: _propTypes.default.string,
  /**
   * Extra class for the textarea element.
  */
  inputClassName: _propTypes.default.string
};
const defaultProps = exports.defaultProps = {
  rows: 10,
  messageType: 'error'
};
InputArea.propTypes = propTypes;
var _default = exports.default = InputArea;